import React from 'react';
import { motion } from 'framer-motion';
import { Star, Heart, Award, Sparkles } from 'lucide-react';

const BridalCollection = () => {
  const collections = [
    {
      name: "Ethereal Dreams 2024",
      image: "https://images.unsplash.com/photo-1594552072238-b8a33785b261?ixlib=rb-1.2.1&auto=format&fit=crop&w=1000&q=80",
      description: "A stunning collection of ethereal wedding gowns featuring delicate lace and flowing silhouettes."
    },
    {
      name: "Royal Romance",
      image: "https://images.unsplash.com/photo-1595777457583-95e059d581b8?ixlib=rb-1.2.1&auto=format&fit=crop&w=1000&q=80",
      description: "Luxurious ball gowns inspired by royal weddings and timeless elegance."
    },
    {
      name: "Modern Minimalist",
      image: "https://images.unsplash.com/photo-1596436889106-be35e843f974?ixlib=rb-1.2.1&auto=format&fit=crop&w=1000&q=80",
      description: "Clean lines and contemporary designs for the modern bride."
    },
    {
      name: "Vintage Revival",
      image: "https://images.unsplash.com/photo-1595777457583-95e059d581b8?ixlib=rb-1.2.1&auto=format&fit=crop&w=1000&q=80",
      description: "Nostalgic designs reimagined for today's romantic celebrations."
    }
  ];

  const features = [
    {
      icon: <Star className="h-8 w-8 text-emerald-400" />,
      title: "Bespoke Design",
      description: "Each gown is meticulously crafted to perfectly fit your unique style and body."
    },
    {
      icon: <Heart className="h-8 w-8 text-emerald-400" />,
      title: "Premium Materials",
      description: "Finest silks, laces, and embellishments sourced from renowned suppliers worldwide."
    },
    {
      icon: <Award className="h-8 w-8 text-emerald-400" />,
      title: "Expert Craftsmanship",
      description: "Skilled artisans with decades of experience in haute couture bridal wear."
    },
    {
      icon: <Sparkles className="h-8 w-8 text-emerald-400" />,
      title: "Personal Styling",
      description: "Dedicated styling consultations to create your perfect bridal look."
    }
  ];

  const testimonials = [
    {
      quote: "My dream dress became a reality. The attention to detail was beyond my expectations.",
      author: "Emma Thompson",
      date: "March 2024"
    },
    {
      quote: "The entire experience was magical. I felt like royalty throughout the process.",
      author: "Sofia Rodriguez",
      date: "February 2024"
    },
    {
      quote: "Absolutely stunning craftsmanship. Every detail was perfect.",
      author: "Claire Bennett",
      date: "January 2024"
    }
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      className="pt-20"
    >
      {/* Hero Section */}
      <section className="relative h-[70vh] flex items-center">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1595777457583-95e059d581b8?ixlib=rb-1.2.1&auto=format&fit=crop&w=2000&q=80')"
          }}
        >
          <div className="absolute inset-0 bg-black/60"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            <h1 className="text-5xl md:text-7xl font-bold mb-6">Bridal Collection</h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Crafting timeless elegance for your perfect day
            </p>
          </motion.div>
        </div>
      </section>

      {/* Featured Collections */}
      <section className="py-24 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Latest Collections</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Discover our newest bridal collections, each piece crafted with love and attention to detail
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {collections.map((collection, index) => (
              <motion.div
                key={collection.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="group relative overflow-hidden rounded-2xl"
              >
                <div className="aspect-w-16 aspect-h-9">
                  <img
                    src={collection.image}
                    alt={collection.name}
                    className="object-cover w-full h-full group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent flex items-end p-6">
                  <div>
                    <h3 className="text-2xl font-bold mb-2">{collection.name}</h3>
                    <p className="text-gray-300">{collection.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-24 bg-black/95">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Our Commitment</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Experience the difference of true craftsmanship and dedication
            </p>
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="text-center p-6 rounded-2xl border border-gray-800 hover:border-emerald-400/50 transition-colors"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-400/10 mb-6">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold mb-2">{feature.title}</h3>
                <p className="text-gray-400">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Bride Stories</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Hear from our beautiful brides about their experience
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="p-8 rounded-2xl border border-gray-800 hover:border-emerald-400/50 transition-colors"
              >
                <div className="flex gap-2 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 fill-emerald-400 text-emerald-400" />
                  ))}
                </div>
                <p className="text-lg mb-6">"{testimonial.quote}"</p>
                <div>
                  <p className="font-semibold">{testimonial.author}</p>
                  <p className="text-sm text-emerald-400">{testimonial.date}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-24 bg-black/95">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">Start Your Journey</h2>
          <p className="text-gray-400 mb-8">
            Book a consultation with our bridal experts and begin creating your dream wedding dress
          </p>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="px-8 py-3 bg-emerald-400 text-black font-semibold rounded-full hover:bg-emerald-300 transition-colors"
          >
            Schedule Consultation
          </motion.button>
        </div>
      </section>
    </motion.div>
  );
};

export default BridalCollection; 